import type { ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";

import type { NotificationActionDescriptor } from "../types";

const ACTION_BUTTON_CLS =
  "inline-flex size-7 items-center justify-center rounded-lg bg-black/15 opacity-95 transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-black/25 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40 motion-reduce:active:scale-100";

function ActionTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

export function NotificationActions({
  actions,
  className,
}: {
  actions: NotificationActionDescriptor[];
  className?: string;
}) {
  if (actions.length === 0) return null;

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {actions.map((action) => {
        const Icon = action.icon ?? ArrowUpRight;
        const tooltip = action.disabledReason ?? action.label;
        return (
          <ActionTooltip key={action.key} label={tooltip}>
            <button
              type="button"
              disabled={action.disabled}
              className={cn(
                ACTION_BUTTON_CLS,
                action.variant === "destructive" &&
                  "text-destructive hover:bg-destructive/15",
              )}
              aria-label={action.label}
              onClick={(event) => {
                event.stopPropagation();
                void Promise.resolve(action.onClick());
              }}
            >
              <Icon className="size-[1.125rem] shrink-0" aria-hidden />
            </button>
          </ActionTooltip>
        );
      })}
    </div>
  );
}
